import React from 'react';
import { Link } from 'react-router-dom';
import { allBlogPostsQuery } from '../lib/queries';
import { useSanityPreview } from '../lib/useSanityPreview';
import CreatorProfile from './CreatorProfile';

interface BlogPost {
  _id: string;
  title: string;
  slug: {
    current: string;
  };
  publishedDate: string;
  author: string;
  authorImageUrl?: string;
  contentPreview?: string;
  featuredImageUrl: string;
  postType: string;
  tags: string[];
}

const JournalGrid: React.FC = () => {
  const { data: posts, error, isLoading } = useSanityPreview<BlogPost[]>(allBlogPostsQuery);

  if (isLoading) {
    return <div className="w-full h-screen flex items-center justify-center">Loading journal posts...</div>;
  }

  if (error) {
    return <div className="w-full h-screen flex items-center justify-center text-red-600">Error loading journal posts: {error.message}</div>;
  }

  if (!posts || posts.length === 0) {
    return <div className="w-full h-screen flex items-center justify-center">No journal posts found</div>;
  }

  // Separate the featured (most recent) post from the rest
  const featuredPost = posts[0];
  const remainingPosts = posts.slice(1);

  // Format date function
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-[2000px] mx-auto px-6">
        <h1 className="text-4xl font-book-antiqua font-bold mb-12 text-center">Weaving Thought</h1>
        
        {/* Featured Post (Full Width) */}
        {featuredPost && (
          <div className="mb-16 border-b border-gray-200 pb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="aspect-[4/3] overflow-hidden">
                <Link to={`/journal/${featuredPost.slug?.current}`}>
                  <img
                    src={featuredPost.featuredImageUrl}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </Link>
              </div>
              <div className="flex flex-col justify-between">
                <div>
                  <h2 className="text-3xl font-ogg font-light italic mb-4 blog-post-title">
                    <Link to={`/journal/${featuredPost.slug?.current}`} className="hover:text-gray-600 transition-colors duration-300">
                      {featuredPost.title}
                    </Link>
                  </h2>
                  <div className="mb-4 text-gray-600 text-sm">
                    {featuredPost.publishedDate && (
                      <span>{formatDate(featuredPost.publishedDate)}</span>
                    )}
                    {featuredPost.postType && (
                      <span className="ml-2 px-2 py-1 bg-gray-100 rounded-sm">{featuredPost.postType}</span>
                    )}
                  </div>
                  {featuredPost.contentPreview && (
                    <div className="prose prose-lg max-w-none mb-6">
                      <p>{featuredPost.contentPreview}</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    {featuredPost.author && (
                      <CreatorProfile 
                        name={featuredPost.author}
                        imageUrl={featuredPost.authorImageUrl}
                        size="sm"
                        showName={true}
                      />
                    )}
                  </div>
                  <Link 
                    to={`/journal/${featuredPost.slug?.current}`} 
                    className="inline-block px-4 py-2 border border-black hover:bg-black hover:text-white transition-colors duration-300 text-sm"
                  >
                    Read More
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Grid of Remaining Posts (3 columns) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {remainingPosts.map((post) => (
            <div key={post._id} className="flex flex-col h-full">
              <div className="aspect-[4/3] overflow-hidden mb-4">
                <Link to={`/journal/${post.slug?.current}`}>
                  <img
                    src={post.featuredImageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </Link>
              </div>
              <div className="flex flex-col flex-grow">
                <div className="mb-2 text-gray-600 text-sm">
                  {post.publishedDate && (
                    <span>{formatDate(post.publishedDate)}</span>
                  )}
                  {post.postType && (
                    <span className="ml-2 px-2 py-1 bg-gray-100 rounded-sm">{post.postType}</span>
                  )}
                </div>
                <h3 className="text-xl font-ogg font-light not-italic mb-3 blog-post-title">
                  <Link to={`/journal/${post.slug?.current}`} className="hover:text-gray-600 transition-colors duration-300">
                    {post.title}
                  </Link>
                </h3>
                {post.contentPreview && (
                  <div className="prose prose-sm max-w-none mb-4 flex-grow">
                    <p className="line-clamp-3">{post.contentPreview}</p>
                  </div>
                )}
                <div className="mt-auto flex justify-between items-center">
                  <div>
                    {post.author && (
                      <CreatorProfile 
                        name={post.author}
                        imageUrl={post.authorImageUrl}
                        size="xs"
                        showName={false}
                      />
                    )}
                  </div>
                  <Link 
                    to={`/journal/${post.slug?.current}`} 
                    className="text-sm underline hover:text-gray-600 transition-colors duration-300"
                  >
                    Read More
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default JournalGrid;
